import React from 'react';
import Plot from 'react-plotly.js';

// Fixed color dictionaries
const consequenceColorMap = {
  "missense_variant": '#e6194b',
  "synonymous_variant": '#3cb44b',
  "stop_gained": '#ffe119',
  "frameshift_variant": '#4363d8',
  "inframe_insertion": '#f58231',
  "start_lost": '#911eb4',
  "splice_region_variant": '#46f0f0',
  "inframe_deletion": '#f032e6',
  "protein_altering_variant": '#bcf60c',
  "NMD_transcript_variant": '#fabebe',
  "coding_sequence_variant": '#008080',
  "incomplete_terminal_codon_variant": '#e6beff',
  "splice_acceptor_variant": '#9a6324',
  "stop_retained_variant": '#fffac8',
  "splice_donor_region_variant": '#800000',
  "start_retained_variant": '#aaffc3',
  "splice_donor_5th_base_variant": '#808000'
};

const aaColorMap = {
  "benign": "blue",
  "pathogenic": "red",
  "ambiguous": "#FFCE56",
  "not_provided": "#4BC0C0",
  "no_prediction": "#4BC0C0",
  "likely_pathogenic": "red",
  "likely_benign": "blue",
};

const clinicalColorMap = {
  'benign': "blue",
  'likely_benign': '#268bff',
  'conflicting_interpretations_of_pathogenicity': '#bd3d3d',
  'likely_pathogenic': '#b41414',
  'pathogenic': "red",
  'risk_factor': '#00c8a8',
  'not_provided': '#2d572c',
  'other': '#666666',
  'no_clinical_significance': '#BC0C5D',
  'uncertain_significance': '#c8aa00'
}

const PtmMap = { 'ub': 'bf812d', 'ac': '5aae61', 'p': '4393C3', 'other': '666666' };

const functionalColorMap = {
  'Ligand Binding Site': '#1f77b4',
  'Active Site': '#ff7f0e',
  'Domains': '#2ca02c',
  'PTMs': '#d62728',
  'Binding Region': '#9467bd',
  'InterPro Domains': '#8c564b'
};

const getColor = (label, type) => {
  if (type === 'consequence') {
    return consequenceColorMap[label] || '#000000';
  } else if (type === 'aa') {
    return aaColorMap[label] || '#000000';
  } else if (type === 'clinical') {
    return clinicalColorMap[label] || '#000000';
  } else if (type === 'ptm') {
    return PtmMap[label] || "66D8C9";
  } else if (type === 'functional') {
    return functionalColorMap[label] || '#000000';
  }
  return '#000000';
};

const BarChartVariant = ({ VariantData, title, consequence, clinical, aa, selectedVariants, positionSummary }) => {

  // Count occurrences of each protein position
  const countVariantsByPosition = (variants) => {
    const counts = {};
    variants.forEach((variant) => {
      counts[variant.proteinPosition] = (counts[variant.proteinPosition] || 0) + 1;
    });
    return counts;
  };

  // Get the top 10 protein positions
  const getTop10Positions = (counts) => {
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([position]) => parseInt(position));
  };

  // Calculate the positions based on selected variants
 const getPositionsBasedOnSelection = (variants) => {
  // Get all unique positions from the variants
  const availablePositions = [...new Set(variants.map(v => parseInt(v.proteinPosition)))].sort((a, b) => a - b);

  if (!selectedVariants || (selectedVariants.length === 0 && !positionSummary)) {
    // If no variant is selected, return the top 10 most frequent positions
    const variantCounts = countVariantsByPosition(variants);
    return getTop10Positions(variantCounts);
  } else if (selectedVariants.length === 1) {
    // If only one variant is selected, find 5 before and 5 after within available positions
    const selectedPosition = selectedVariants[0];

    // Find the index of the selected position in the available positions list
    const index = availablePositions.indexOf(selectedPosition);
    if (index === -1) return [selectedPosition]; // Edge case where position is not found

    // Select up to 5 before and 5 after, without exceeding array bounds
    const start = Math.max(0, index - 5);
    const end = Math.min(availablePositions.length, index + 6);

    return availablePositions.slice(start, end);
  } else if (positionSummary && (!selectedVariants || selectedVariants.length === 0)) {
    // If positionSummary is set, apply the same logic
    const position = parseInt(positionSummary);
    const index = availablePositions.indexOf(position);
    if (index === -1) return [position];

    const start = Math.max(0, index - 5);
    const end = Math.min(availablePositions.length, index + 6);

    return availablePositions.slice(start, end);
  } else {
    // If multiple variants are selected, return their exact positions
    return [...new Set(selectedVariants)];
  }
};


  const topPositions = getPositionsBasedOnSelection(VariantData);

  // Sort top positions only if selectedVariants is empty
  if (!selectedVariants || selectedVariants.length === 0) {
    topPositions.sort((a, b) => a - b); // Sort positions in ascending order
  }

  // Filter the arrays based on data found in the top positions
  const filterValues = (variants, key, values = []) => {
    const foundValues = new Set();

    variants.forEach((variant) => {
      if (topPositions.includes(parseInt(variant.proteinPosition))) {
        variant[key]?.split(',').forEach((item) => {
          foundValues.add(item.trim().toUpperCase());
        });
      }
    });

    // Return filtered array based on found values in the top positions
    return values.filter(value => foundValues.has(value.label.trim().toUpperCase()));
  };

  // Applying the filterValues function to filter the arrays
  const filteredConsequence = filterValues(VariantData, 'consequence', consequence);
  const filteredClinical = filterValues(VariantData, 'clinicalSignificance', clinical);
  const filteredAA = filterValues(VariantData, 'alphamissenseClass', aa);

  // Count occurrences of each label for each top protein position
  const getCountsByPosition = (variants, key, values, positions) => {
    return values.map((value) => {
      return positions.map((position) => {
        // Handle multiple labels within a single field using splitting
        const count = variants.filter((variant) => {
          const variantLabels = variant[key].split(',').map(item => item.toUpperCase().trim());
          return variantLabels.includes(value.label.toUpperCase().trim()) && parseInt(variant.proteinPosition) === parseInt(position);
        }).length;
        return count;
      });
    });
  };


// Function to count domains per position once
const countDomains = (variants, key, position) => {
  const uniqueDomains = new Set();
  variants.forEach(variant => {
    if (parseInt(variant.proteinPosition) === position && variant[key]) {
      const values = variant[key].split(/(?=[A-Z]) /).map(val => val.trim());
      values.filter(val => val && !val.startsWith('-')).forEach(domain => uniqueDomains.add(domain));
    }
  });
  return uniqueDomains.size;
};

// Function to count domains per position once
const countInteProtDomains = (variants, key, position) => {
  const uniqueDomains = new Set();
  variants.forEach(variant => {
    if (parseInt(variant.proteinPosition) === position && variant[key]) {
      const values = variant[key].split(";").map(val => val.trim());
      values.filter(val => val && !val.startsWith('-')).forEach(domain => uniqueDomains.add(domain));
    }
  });
  return uniqueDomains.size;
};

// Function to count unique other labels for a specific position
const countOtherLabels = (variants, key, position) => {
  const uniqueLabels = new Set();
  variants.forEach(variant => {
    if (parseInt(variant.proteinPosition) === position && variant[key]) {
      const labels = variant[key].split(/[ \t ]+/).map(val => val.trim().toLowerCase());
      labels.forEach(label => {
        if (label && !label.startsWith('-')) {
          uniqueLabels.add(label);
        }
      });
    }
  });
  return uniqueLabels.size;
};

const countBidingRegion = (variants, key, position) => {
  const uniqueLabels = new Set();
  variants.forEach(variant => {
    if (parseInt(variant.proteinPosition) === position && variant[key]) {
      const bindingRegionRegex = /[A-Z0-9]+\(\d+-\d+\)_[A-Z0-9]+\(\d+-\d+\)_[\w\s]+\(MI SCORE:\d+\.\d+\)/g; // Added 'g' flag for global matching
      const matches = variant[key].match(bindingRegionRegex);
      if (matches) {
        matches.forEach(match => {
          if (match && !match.startsWith('-')) {
            uniqueLabels.add(match.toLowerCase().trim()); // Convert to lowercase and trim
          }
        });
      }
    }
  });
  return uniqueLabels.size;
};

// Function to count unique labels (excluding those starting with "-") at a specific protein position
const countActiveSite = (variants, key, targetPosition) => {
  const position = parseInt(targetPosition);

  for (const variant of variants) {
    const variantPosition = parseInt(variant.proteinPosition);
    const label = variant[key]?.trim().toLowerCase();

    if (
      variantPosition === position &&
      label &&
      label !== '-'
    ) {
      return 1;
    }
  }

  return 0;
};

const countBindingSite = (variants, key, targetPosition) => {
  const position = parseInt(targetPosition);

  for (const variant of variants) {
    const variantPosition = parseInt(variant.proteinPosition);
    const label = variant[key]?.trim().toLowerCase();

    if (
      variantPosition === position &&
      label &&
      label !== '-'
    ) {
      return 1;
    }
  }

  return 0;
};


  let plotlyData = [];

  if (title === 'Consequence') {
    const counts = getCountsByPosition(VariantData, 'consequence', filteredConsequence, topPositions);
    plotlyData = filteredConsequence.map((value, index) => ({
      x: topPositions,
      y: counts[index],
      name: value.label,
      type: 'bar',
      marker: { color: getColor(value.label, 'consequence') }
    }));
  } else if (title === 'Clinical Significance') {
    const counts = getCountsByPosition(VariantData, 'clinicalSignificance', filteredClinical, topPositions);
    plotlyData = filteredClinical.map((value, index) => ({
      x: topPositions,
      y: counts[index],
      name: value.label,
      type: 'bar',
      marker: { color: getColor(value.label, 'clinical') }
    }));
  } else if (title === 'AlphaMissense class') {
    const counts = getCountsByPosition(VariantData, 'alphamissenseClass', filteredAA, topPositions);
    plotlyData = filteredAA.map((value, index) => ({
      x: topPositions,
      y: counts[index],
      name: value.label,
      type: 'bar',
      marker: { color: getColor(value.label, 'aa') }
    }));
  } else if (title === 'Intra-protein contact (AlphaFold)') {
    const topPositionsSet = new Set(topPositions);
    const filteredAFContact = VariantData
      .filter(variant => topPositionsSet.has(parseInt(variant.proteinPosition)))
      .filter((value, index, self) => index === self.findIndex((t) => t.proteinPosition === value.proteinPosition));

    plotlyData = [{
      x: filteredAFContact.map(variant => variant.afMaxPredictedNumInteractionResidue),
      y: topPositions,
      name: "Number of affected contact",
      type: 'bar',
      orientation: 'h',
      marker: { color: "#008000" }
    }];
  } else if (title === 'Functional Impact') {
    const functionalKeys = ['unLigandsBindingSite', 'unAnnPropActiveSite', 'unDomains', 'phosPtms', 'inBindingRegion', 'interproDomain'];
    const functionalLabels = ['Ligand Binding Site', 'Active Site', 'Domains', 'PTMs', 'Binding Region', 'InterPro Domains'];

    plotlyData = functionalKeys.map((key, index) => {
      const counts = topPositions.map(position => {
        if (key === 'unDomains') return countDomains(VariantData, key, position);
        if (key === 'interproDomain') return countInteProtDomains(VariantData, key, position);
        if (key === 'inBindingRegion') return countBidingRegion(VariantData, key, position);
        if (key === 'unAnnPropActiveSite') return countActiveSite(VariantData, key, position);
        if (key === 'unLigandsBindingSite') return countBindingSite(VariantData, key, position);
        return countOtherLabels(VariantData, key, position);
      });

      return {
        x: topPositions,
        y: counts,
        name: functionalLabels[index],
        type: 'bar',
        marker: { color: getColor(functionalLabels[index], 'functional') }
      };
    });
  }

  return (
    <div className="card" style={{ width: '100%' }}>

      <div style={{ width: '100%', height: '500px' }}>
        <Plot
          data={plotlyData}
          layout={{
            barmode: 'stack',
            autosize: true,
            title: {
              text: title,
            },
            margin: { l: 30, r: 10, t: 80, b: 80 },
            showlegend: true,
           legend: {
            orientation: 'h'
            },
            xaxis: { title: 'Count', gridcolor: '#ebedef', type: 'category' },
            yaxis: { title: 'Protein Position', gridcolor: '#ebedef' }
          }}
          useResizeHandler={true}
          style={{ width: "100%", height: "100%" }}
          config={{ responsive: true}}
        />
      </div>
    </div>
  );
};

export default BarChartVariant;